import { useCallback, useEffect, useMemo, useState } from 'react'
import { Boxes, RefreshCw, Plus, Check, X } from 'lucide-react'
import {
  fetchAllCreditPackages,
  createCreditPackage,
  updateCreditPackage,
} from '../api/client'
import type { CreditPackage } from '../api/types'

const CURRENCIES = ['USD', 'TRY'] as const

type Status = 'loading' | 'ready' | 'error'

/** A blank package draft for the create form. */
type Draft = {
  name: string
  reads: string
  price: string
  currency: string
  sort_order: string
}

const EMPTY_DRAFT: Draft = { name: '', reads: '', price: '', currency: 'USD', sort_order: '0' }

export default function AdminPackagesPage() {
  const [packages, setPackages] = useState<CreditPackage[]>([])
  const [status, setStatus] = useState<Status>('loading')
  const [error, setError] = useState<string | null>(null)

  // Create form
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT)
  const [creating, setCreating] = useState(false)

  // Inline edit
  const [editId, setEditId] = useState<number | null>(null)
  const [editDraft, setEditDraft] = useState<Draft>(EMPTY_DRAFT)
  const [savingId, setSavingId] = useState<number | null>(null)

  const load = useCallback(() => {
    setStatus('loading')
    fetchAllCreditPackages()
      .then((d) => {
        setPackages(d.packages || [])
        setStatus('ready')
      })
      .catch((e) => {
        setError((e as Error).message || 'Paketler yüklenemedi')
        setStatus('error')
      })
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const sorted = useMemo(
    () => [...packages].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.price - b.price),
    [packages],
  )

  async function handleCreate() {
    setError(null)
    const reads = Number(draft.reads)
    const price = Number(draft.price)
    if (!draft.name.trim() || !Number.isFinite(reads) || reads <= 0 || !Number.isFinite(price) || price < 0) {
      setError('Ad, okuma kredisi (>0) ve fiyat (≥0) zorunlu')
      return
    }
    setCreating(true)
    try {
      await createCreditPackage({
        name: draft.name.trim(),
        reads,
        price,
        currency: draft.currency,
        sort_order: Number(draft.sort_order) || 0,
      })
      setDraft(EMPTY_DRAFT)
      load()
    } catch (e) {
      setError((e as Error).message || 'Paket oluşturulamadı')
    } finally {
      setCreating(false)
    }
  }

  function startEdit(pkg: CreditPackage) {
    setEditId(pkg.id)
    setEditDraft({
      name: pkg.name,
      reads: String(pkg.reads),
      price: String(pkg.price),
      currency: pkg.currency || 'USD',
      sort_order: String(pkg.sort_order ?? 0),
    })
  }

  function cancelEdit() {
    setEditId(null)
  }

  async function saveEdit(id: number) {
    setError(null)
    const reads = Number(editDraft.reads)
    const price = Number(editDraft.price)
    if (!editDraft.name.trim() || !Number.isFinite(reads) || reads <= 0 || !Number.isFinite(price) || price < 0) {
      setError('Ad, okuma kredisi (>0) ve fiyat (≥0) zorunlu')
      return
    }
    setSavingId(id)
    try {
      await updateCreditPackage(id, {
        name: editDraft.name.trim(),
        reads,
        price,
        currency: editDraft.currency,
        sort_order: Number(editDraft.sort_order) || 0,
      })
      setEditId(null)
      load()
    } catch (e) {
      setError((e as Error).message || 'Paket güncellenemedi')
    } finally {
      setSavingId(null)
    }
  }

  async function toggleActive(pkg: CreditPackage) {
    setError(null)
    setSavingId(pkg.id)
    try {
      await updateCreditPackage(pkg.id, { is_active: !pkg.is_active })
      load()
    } catch (e) {
      setError((e as Error).message || 'Durum değiştirilemedi')
    } finally {
      setSavingId(null)
    }
  }

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-24">
        <div className="flex items-center gap-12">
          <Boxes size={22} />
          <h1 className="text-xl font-bold">Kredi Paketleri</h1>
        </div>
        <button className="btn btn-outline btn-sm" onClick={load} disabled={status === 'loading'}>
          <RefreshCw size={14} className={status === 'loading' ? 'spin' : ''} />
          Yenile
        </button>
      </div>

      {error && (
        <div className="card card-padding mb-16" style={{ borderColor: 'var(--color-danger)' }}>
          <p className="text-sm" style={{ color: 'var(--color-danger)' }}>{error}</p>
        </div>
      )}

      {/* Create new package */}
      <div className="card card-padding mb-24">
        <div className="card-header">
          <h2 className="card-title">Yeni Paket</h2>
        </div>
        <div className="form-row">
          <div className="form-group" style={{ flex: 3 }}>
            <label className="form-label">Ad</label>
            <input
              className="form-input"
              placeholder="Başlangıç"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
          </div>
          <div className="form-group" style={{ flex: 2 }}>
            <label className="form-label">Okuma Kredisi</label>
            <input
              className="form-input"
              type="number"
              min="0"
              step="any"
              placeholder="100"
              value={draft.reads}
              onChange={(e) => setDraft({ ...draft, reads: e.target.value })}
            />
          </div>
          <div className="form-group" style={{ flex: 2 }}>
            <label className="form-label">Fiyat</label>
            <input
              className="form-input"
              type="number"
              min="0"
              step="any"
              placeholder="10"
              value={draft.price}
              onChange={(e) => setDraft({ ...draft, price: e.target.value })}
            />
          </div>
          <div className="form-group" style={{ flex: 2 }}>
            <label className="form-label">Para Birimi</label>
            <select
              className="form-select"
              value={draft.currency}
              onChange={(e) => setDraft({ ...draft, currency: e.target.value })}
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label">Sıra</label>
            <input
              className="form-input"
              type="number"
              step="1"
              value={draft.sort_order}
              onChange={(e) => setDraft({ ...draft, sort_order: e.target.value })}
            />
          </div>
        </div>
        <button className="btn btn-primary btn-sm" style={{ marginTop: 16 }} onClick={handleCreate} disabled={creating}>
          <Plus size={14} />
          {creating ? 'Ekleniyor...' : 'Paket Ekle'}
        </button>
      </div>

      {/* Existing packages */}
      <div className="card">
        <div className="card-header" style={{ padding: '20px 24px 0' }}>
          <h2 className="card-title">Mevcut Paketler</h2>
        </div>

        {status === 'loading' && packages.length === 0 ? (
          <div className="card-padding"><p className="text-muted">Yükleniyor...</p></div>
        ) : sorted.length === 0 ? (
          <div className="card-padding"><p className="text-muted">Henüz paket yok.</p></div>
        ) : (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>Ad</th>
                  <th style={{ textAlign: 'right' }}>Okuma Kredisi</th>
                  <th style={{ textAlign: 'right' }}>Fiyat</th>
                  <th>Para Birimi</th>
                  <th style={{ textAlign: 'right' }}>Sıra</th>
                  <th>Durum</th>
                  <th style={{ textAlign: 'right' }}>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((pkg) => {
                  const editing = editId === pkg.id
                  const busy = savingId === pkg.id
                  return (
                    <tr key={pkg.id}>
                      <td>
                        {editing ? (
                          <input
                            className="form-input"
                            value={editDraft.name}
                            onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                          />
                        ) : (
                          <span className="font-semibold">{pkg.name}</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {editing ? (
                          <input
                            className="form-input"
                            type="number"
                            min="0"
                            step="any"
                            style={{ textAlign: 'right' }}
                            value={editDraft.reads}
                            onChange={(e) => setEditDraft({ ...editDraft, reads: e.target.value })}
                          />
                        ) : (
                          pkg.reads.toLocaleString()
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {editing ? (
                          <input
                            className="form-input"
                            type="number"
                            min="0"
                            step="any"
                            style={{ textAlign: 'right' }}
                            value={editDraft.price}
                            onChange={(e) => setEditDraft({ ...editDraft, price: e.target.value })}
                          />
                        ) : (
                          pkg.price.toFixed(2)
                        )}
                      </td>
                      <td>
                        {editing ? (
                          <select
                            className="form-select"
                            value={editDraft.currency}
                            onChange={(e) => setEditDraft({ ...editDraft, currency: e.target.value })}
                          >
                            {CURRENCIES.map((c) => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                        ) : (
                          pkg.currency
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {editing ? (
                          <input
                            className="form-input"
                            type="number"
                            step="1"
                            style={{ textAlign: 'right', width: 72 }}
                            value={editDraft.sort_order}
                            onChange={(e) => setEditDraft({ ...editDraft, sort_order: e.target.value })}
                          />
                        ) : (
                          pkg.sort_order ?? 0
                        )}
                      </td>
                      <td>
                        <button
                          className={`badge ${pkg.is_active ? 'badge-success' : 'badge-neutral'}`}
                          style={{ cursor: 'pointer', border: 'none' }}
                          disabled={busy || editing}
                          onClick={() => toggleActive(pkg)}
                          title="Aktiflik durumunu değiştir"
                        >
                          {pkg.is_active ? 'Aktif' : 'Pasif'}
                        </button>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {editing ? (
                          <div className="flex items-center gap-8" style={{ justifyContent: 'flex-end' }}>
                            <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => saveEdit(pkg.id)}>
                              <Check size={14} />
                              {busy ? 'Kaydediliyor...' : 'Kaydet'}
                            </button>
                            <button className="btn btn-outline btn-sm" disabled={busy} onClick={cancelEdit}>
                              <X size={14} />
                              İptal
                            </button>
                          </div>
                        ) : (
                          <button className="btn btn-outline btn-sm" disabled={busy} onClick={() => startEdit(pkg)}>
                            Düzenle
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
